import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Star, MapPin, Calendar, Clock, Plane, ChevronLeft, ChevronRight, Search, Filter, X } from 'lucide-react'
import { usePackages, PackageFilters } from '../hooks/usePackages'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { usePackageTypes } from '../hooks/usePackageTypes'
import { useDestinations } from '../hooks/useDestinations'
import PageHeader from '../components/PageHeader'
import Seo from '../components/Seo'

const categories = ['All', 'Luxury', 'Golf', 'Beach', 'City Break', 'Adults Only', 'Eco-Friendly']

export default function Packages() {
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [searchTerm, setSearchTerm] = useState('')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  
  // Advanced filter states
  const [selectedDestination, setSelectedDestination] = useState('')
  const [selectedPackageType, setSelectedPackageType] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [priceRange, setPriceRange] = useState('')

  // Read initial page from URL
  const queryParams = new URLSearchParams(window.location.search)
  const initialPage = parseInt(queryParams.get('page') || '1', 10)

  // Function to update URL with new page
  const updateUrlPage = (page: number) => {
    window.history.pushState({}, '', `/packages?page=${page}`)
  }
  
  const { franchiseData, loading: franchiseLoading } = useFranchiseData()
  const { packageTypes, loading: packageTypesLoading } = usePackageTypes()
  const { destinations, loading: destinationsLoading } = useDestinations()
  const { 
    packages, 
    pagination, 
    loading: packagesLoading, 
    error,
    applyFilters,
    goToNextPage,
    goToPrevPage,
    loadPage 
  } = usePackages({ initialPage }) // Pass initial page properly

  const loading = franchiseLoading || packagesLoading

  // Override pagination functions to update URL
  const goToNextPageWithUrl = () => {
    if (pagination.has_next) {
      const nextPage = pagination.current_page + 1
      loadPage(nextPage)
      updateUrlPage(nextPage)
    }
  }

  const goToPrevPageWithUrl = () => {
    if (pagination.has_prev) {
      const prevPage = pagination.current_page - 1
      loadPage(prevPage)
      updateUrlPage(prevPage)
    }
  }

  const loadPageWithUrl = (page: number) => {
    loadPage(page)
    updateUrlPage(page)
  }

  const buildFilters = (searchOverride?: string): PackageFilters => {
    const filters: PackageFilters = {}

    if (selectedCategory !== 'All') {
      filters.category = selectedCategory
    }

    const search = searchOverride ?? searchTerm
    if (search) {
      filters.search = search
    }
    
    if (selectedDestination) {
      filters.destination = parseInt(selectedDestination)
    }
    
    if (selectedPackageType) {
      filters.offer_type = parseInt(selectedPackageType)
    }
    
    if (dateFrom) {
      filters.date_from = dateFrom
    }
    
    if (dateTo) {
      filters.date_to = dateTo
    }
    
    if (priceRange) {
      filters.price = priceRange
    }
    
    return filters
  }

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
    applyFilters(buildFilters())
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters(buildFilters())
  }

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const hadTerm = searchTerm.trim().length > 0
    setSearchTerm(value)

    // Emptying the box (backspace, cut, select-all delete) clears the previous
    // search immediately and restores the listing - other filters stay active.
    // Typing a non-empty term still waits for submit.
    if (!value.trim() && hadTerm) {
      applyFilters(buildFilters(''))
    }
  }

  const handleAdvancedFilterApply = () => {
    applyFilters(buildFilters())
    setShowAdvancedFilters(false)
  }

  const clearAllFilters = () => {
    setSelectedCategory('All')
    setSearchTerm('')
    setSelectedDestination('')
    setSelectedPackageType('')
    setDateFrom('')
    setDateTo('')
    setPriceRange('')
    applyFilters({})
  }

  const hasActiveFilters = selectedCategory !== 'All' || searchTerm || selectedDestination || selectedPackageType || dateFrom || dateTo || priceRange

  const getImageUrl = (pkg: any) => {
    if (pkg.cover_photo?.large_url) {
      return pkg.cover_photo.large_url
    }
    if (pkg.cover_photo?.url) {
      return pkg.cover_photo.url
    }
    if (pkg.scraped_cover_photo) {
      return pkg.scraped_cover_photo
    }
    return null
  }

  const extractNightsFromName = (name: string): number | null => {
    if (!name) return null

    // Look for patterns like "2 nights", "3 night", etc.
    const nightsMatch = name.match(/(\d+)\s+nights?/i)
    if (nightsMatch) {
      return parseInt(nightsMatch[1], 10)
    }

    return null
  }

  const getNights = (pkg: any): number | null => {
    // Use the nights field if it exists and is not null
    if (pkg.nights !== null && pkg.nights !== undefined) {
      return pkg.nights
    }

    // Try to extract from name
    return extractNightsFromName(pkg.name)
  }

  const getDuration = (pkg: any): string => {
    const nights = getNights(pkg)
    if (nights) {
      return `${nights} ${nights === 1 ? 'Night' : 'Nights'}`
    }

    // If we have a duration field and it's not showing 'null nights' or similar issues
    if (pkg.duration && !pkg.duration.toLowerCase().includes('null')) {
      return pkg.duration
    }

    // Fallback
    return 'Duration TBA'
  }

  const formatDate = (dateString: string): string => {
    if (!dateString || dateString === 'null') return ''

    try {
      // Handle various date formats
      const date = new Date(dateString)

      // Check if date is valid (not 1970 epoch)
      if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
        return ''
      }

      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    } catch {
      return ''
    }
  }

  const getDates = (pkg: any): string => {
    // First try to use the dates field if it exists and is not null/empty
    if (pkg.dates && pkg.dates !== 'null' && pkg.dates.trim() !== '') {
      return pkg.dates
    }

    // Then try travel_from and travel_to
    if (pkg.travel_from && pkg.travel_to && pkg.travel_from !== 'null' && pkg.travel_to !== 'null') {
      const fromDate = formatDate(pkg.travel_from)
      const toDate = formatDate(pkg.travel_to)
      if (fromDate && toDate) {
        return `${fromDate} - ${toDate}`
      }
    }

    // Fall back to validity dates
    if (pkg.valid_from && pkg.valid_to && pkg.valid_from !== 'null' && pkg.valid_to !== 'null') {
      const fromDate = formatDate(pkg.valid_from)
      const toDate = formatDate(pkg.valid_to)
      if (fromDate && toDate) {
        return `Valid: ${fromDate} - ${toDate}`
      }
    }

    // Final fallback
    return 'Dates TBA'
  }

  if (loading && !packages.length) {
    return (
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="mx-auto max-w-2xl text-center">
              <div className="h-10 bg-gray-300 rounded w-64 mx-auto mb-6"></div>
              <div className="h-6 bg-gray-300 rounded w-96 mx-auto"></div>
            </div>
            <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex flex-col items-start">
                  <div className="aspect-[16/9] w-full rounded-2xl bg-gray-300"></div>
                  <div className="mt-8 space-y-3 w-full">
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                    <div className="h-6 bg-gray-300 rounded w-full"></div>
                    <div className="h-16 bg-gray-300 rounded w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Something went wrong
            </h2>
            <p className="mt-6 text-lg leading-8 text-red-600">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-8 btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white">
      <Seo
        title="Travel Packages - Memento Travel Co."
        description="Discover our exclusive collection of carefully curated travel packages to amazing destinations around the world. Find your perfect getaway today."
        canonicalUrl={typeof window !== 'undefined' ? window.location.href : 'https://www.mementotravel.co/packages'}
        pageType="packages"
      />
      <PageHeader
        title="Our Packages"
        description="Discover our exclusive collection of carefully curated travel packages to amazing destinations around the world."
      />
      
      <div className="py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-3">
              Curated Travel Experiences
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              From luxury getaways to adventure escapes, discover packages crafted for unforgettable memories
          </p>
        </div>

        {/* Search and Filter Bar */}
          <div className="max-w-4xl mx-auto mb-12">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex gap-3">
              <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                    placeholder="Search destinations, packages, or experiences..."
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-base"
                />
              </div>
              <button 
                type="button"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={`flex items-center gap-2 px-4 py-3 border rounded-xl transition-all duration-200 ${
                  showAdvancedFilters 
                      ? 'text-white shadow-lg' 
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
                  style={showAdvancedFilters ? {background: '#006D77', borderColor: '#006D77'} : {}}
              >
                <Filter className="h-4 w-4" />
                Filters
              </button>
                <button 
                  type="submit" 
                  className="px-6 py-3 rounded-xl text-white font-medium transition-all duration-200 hover:opacity-90"
                  style={{ background: '#006D77' }}
                >
                Search
              </button>
            </div>

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
                <div className="p-8 border border-gray-200 rounded-2xl bg-gray-50">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-serif font-bold text-gray-900">Advanced Filters</h3>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedFilters(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                      <X className="h-6 w-6" />
                  </button>
                </div>
                
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Destination Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Destination
                    </label>
                    <select
                      value={selectedDestination}
                      onChange={(e) => setSelectedDestination(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    >
                      <option value="">All Destinations</option>
                      {destinations.map((destination) => (
                        <option key={destination.id} value={destination.id}>
                          {destination.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Package Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Package Type
                    </label>
                    <select
                      value={selectedPackageType}
                      onChange={(e) => setSelectedPackageType(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    >
                      <option value="">All Types</option>
                      {packageTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date From */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date From
                    </label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    />
                  </div>

                  {/* Date To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date To
                    </label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    />
                  </div>

                  {/* Price Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price Range
                    </label>
                    <select
                      value={priceRange}
                      onChange={(e) => setPriceRange(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    >
                      <option value="">Any Price</option>
                      <option value="0">Under R5000</option>
                      <option value="5-10">R5000 - R10000</option>
                      <option value="10-20">R10000 - R20000</option>
                      <option value="20">R20000 Upwards</option>
                    </select>
                  </div>
                </div>

                  <div className="flex justify-between mt-8">
                  <button
                    type="button"
                    onClick={clearAllFilters}
                      className="px-6 py-3 text-gray-600 hover:text-gray-900 font-medium transition-colors"
                  >
                    Clear All Filters
                  </button>
                  <button
                    type="button"
                    onClick={handleAdvancedFilterApply}
                      className="px-8 py-3 rounded-xl text-white font-medium transition-all duration-200 hover:opacity-90"
                      style={{ background: '#006D77' }}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>


          {/* Categories
          <div className="flex flex-wrap justify-center gap-3 mb-16">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedCategory === category
                    ? 'text-white shadow-lg transform scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
              }`}
                style={selectedCategory === category ? {background: '#006D77'} : {}}
            >
              {category}
            </button>
          ))}
        </div> */}

        {/* Active Filters Display */}
        {hasActiveFilters && (
            <div className="max-w-4xl mx-auto mb-8">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-gray-600 font-medium">Active filters:</span>
              {selectedCategory !== 'All' && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium" style={{background: '#006D77'}}>
                  Category: {selectedCategory}
                    <X className="h-4 w-4 cursor-pointer hover:opacity-70" onClick={() => setSelectedCategory('All')} />
                </span>
              )}
              {selectedDestination && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                  Destination: {destinations.find(d => d.id.toString() === selectedDestination)?.name}
                    <X className="h-4 w-4 cursor-pointer hover:opacity-70" onClick={() => setSelectedDestination('')} />
                </span>
              )}
              {selectedPackageType && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                  Type: {packageTypes.find(t => t.id.toString() === selectedPackageType)?.name}
                    <X className="h-4 w-4 cursor-pointer hover:opacity-70" onClick={() => setSelectedPackageType('')} />
                </span>
              )}
              {(dateFrom || dateTo) && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 text-purple-800 text-sm font-medium">
                  Dates: {dateFrom} - {dateTo}
                    <X className="h-4 w-4 cursor-pointer hover:opacity-70" onClick={() => { setDateFrom(''); setDateTo('') }} />
                </span>
              )}
              {priceRange && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-gray-800 text-sm font-medium" style={{backgroundColor: '#F7F7E9'}}>
                  Price: {
                    priceRange === '0' ? 'Under R5000' :
                    priceRange === '5-10' ? 'R5000 - R10000' :
                    priceRange === '10-20' ? 'R10000 - R20000' :
                    priceRange === '20' ? 'R20000 Upwards' : ''
                  }
                    <X className="h-4 w-4 cursor-pointer hover:opacity-70" onClick={() => setPriceRange('')} />
                </span>
              )}
              <button
                onClick={clearAllFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 underline font-medium transition-colors"
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Results Info */}
          <div className="text-center text-gray-600 mb-12">
            <p className="text-lg">
              Showing <span className="font-semibold" style={{color: '#006D77'}}>{packages.length}</span> of <span className="font-semibold" style={{color: '#006D77'}}>{pagination.total_count}</span> packages
          {selectedCategory !== 'All' && ` in "${selectedCategory}"`}
          {searchTerm && ` matching "${searchTerm}"`}
            </p>
        </div>

        {/* Packages Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg) => {
            const imageUrl = getImageUrl(pkg)
            
            return (
                <div key={pkg.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <Link to={`/packages/${pkg.slug}`} className="block">
                    <div className="relative h-48 overflow-hidden">
                      <div 
                        className="w-full h-full bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
                      style={{ 
                          backgroundColor: franchiseData?.accent_color || '#F7F7E9',
                        backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                        }}
                      >
                        {!imageUrl && (
                          <div className="flex items-center justify-center h-full">
                            <Plane className="h-16 w-16 text-gray-400" />
                          </div>
                        )}
                    </div>
                      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-gray-700">
                      {pkg.category}
                    </div>
                      {(() => {
                        const nights = getNights(pkg)
                        return nights ? (
                          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium" style={{color: '#006D77'}}>
                            {nights} {nights === 1 ? 'Night' : 'Nights'}
                          </div>
                        ) : null
                      })()}
                  </div>
                </Link>
                
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: pkg.rating || 5 }).map((_, i) => (
                          <Star key={i} className="h-3 w-3" style={{color: '#006D77', fill: '#006D77'}} />
                      ))}
                    </div>
                      <span className="text-xs text-gray-500">•</span>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {pkg.location || 'International'}
                    </span>
                  </div>
                    
                    <Link to={`/packages/${pkg.slug}`}>
                      <h3 className="text-lg font-serif font-bold text-gray-900 mb-2 group-hover:text-[#006D77] transition-colors">
                        {pkg.name}
                      </h3>
                    </Link>
                    
                    <p className="text-gray-600 mb-3 line-clamp-2 text-sm">
                      {pkg.intro || pkg.description}
                    </p>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-600">{getDuration(pkg)}</span>
                  </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg font-bold" style={{color: '#006D77'}}>
                          From {pkg.formatted_price}
                  </div>
                        <div className="text-xs text-gray-500">per person</div>
                    </div>
                      <Link 
                        to={`/packages/${pkg.slug}`} 
                        className="px-4 py-2 rounded-lg text-white font-medium transition-all duration-200 hover:opacity-90 hover:scale-105 text-sm"
                        style={{ background: '#006D77' }}
                      >
                      View Details
                    </Link>
                  </div>
                </div>
                </div>
            )
          })}
        </div>

        {/* No results */}
        {packages.length === 0 && !loading && (
          <div className="mx-auto mt-16 max-w-2xl text-center">
            <h3 className="text-lg font-semibold text-gray-900">No packages found</h3>
            <p className="mt-2 text-sm text-gray-600">
              Try adjusting your search or filter criteria.
            </p>
            <button 
              onClick={clearAllFilters}
              className="mt-4 btn-secondary"
            >
              Clear all filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="mx-auto mt-20 flex items-center justify-center gap-4">
            <button
              onClick={goToPrevPageWithUrl}
              disabled={!pagination.has_prev}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>
            
            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(pagination.total_pages, 7) }, (_, i) => {
                let page
                if (pagination.total_pages <= 7) {
                  page = i + 1
                } else {
                  const current = pagination.current_page
                  if (current <= 4) {
                    page = i + 1
                  } else if (current >= pagination.total_pages - 3) {
                    page = pagination.total_pages - 6 + i
                  } else {
                    page = current - 3 + i
                  }
                }
                
                return (
                  <button
                    key={page}
                    onClick={() => loadPageWithUrl(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      page === pagination.current_page
                        ? 'text-white'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                    style={page === pagination.current_page ? {background: '#006D77'} : {}}
                  >
                    {page}
                  </button>
                )
              })}
            </div>

            <button
              onClick={goToNextPageWithUrl}
              disabled={!pagination.has_next}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        )}

        {/* Loading indicator */}
        {loading && packages.length > 0 && (
          <div className="mx-auto mt-20 text-center">
            <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600">
              <div className="animate-spin h-4 w-4 border-2 border-t-transparent rounded-full mr-2" style={{borderColor: '#006D77', borderTopColor: 'transparent'}}></div>
              Loading...
            </div>
          </div>
        )}

        {/* CTA Section */}
          <div className="mt-20 text-center">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-12">
              <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
                Need a Custom Experience?
            </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                Our travel experts can create a personalized package tailored to your dreams and preferences.
              </p>
              <a 
                href="/contact" 
                className="inline-flex items-center px-8 py-4 rounded-xl text-white font-medium transition-all duration-200 hover:opacity-90 hover:scale-105"
                        style={{ background: '#006D77' }}
              >
                Contact Our Experts
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 